// src/components/AdminProductForm.js
import React, { useState, useRef, useEffect } from 'react';
import { getStores, getCategories } from '../api/apiService';

const API_BASE_URL = 'https://elededanza.com/api';

const bs = { fontFamily: "'Montserrat', sans-serif" };

const inp = {
  ...bs, width: '100%', padding: '9px 12px', border: '1px solid #e5e0e0',
  fontSize: '12px', color: '#060D14', outline: 'none', borderRadius: '2px',
  background: 'white', transition: 'border-color 0.2s', boxSizing: 'border-box',
};
const lbl = {
  ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
  textTransform: 'uppercase', color: '#999', marginBottom: '5px',
};
const fr = e => { e.target.style.borderColor = '#961914'; };
const fb = e => { e.target.style.borderColor = '#e5e0e0'; };

// ── helpers de variantes ───────────────────────────────────────────────────
// Obtiene tallas únicas del array de variantes
const getTallas = (variants) => [...new Set(variants.map(v => v.size).filter(Boolean))];

// Obtiene variantes de una talla específica
const getVariantsByTalla = (variants, talla) => variants.filter(v => v.size === talla);

const Section = ({ title, children }) => (
  <div style={{ background: 'white', border: '1px solid #f0eaea',
    borderRadius: '2px', padding: '20px', marginBottom: '12px' }}>
    <p style={{ ...bs, fontSize: '10px', fontWeight: 700, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: '#961914', marginBottom: '16px' }}>
      {title}
    </p>
    {children}
  </div>
);

const AdminProductForm = ({ product, onSubmit, onCancel }) => {
  const [stores,     setStores]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [uploading,  setUploading]  = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([getStores(), getCategories()]);
        setStores(s); setCategories(c);
      } catch (err) { console.error(err); }
    })();
  }, []);

  const defaultState = {
    name: '', description: '', price: 0, productionCost: 0, lowStockThreshold: 5,
    variants: [{ size: '', color: '', stock: 0, storeStocks: {} }],
    images: [], videos: [], assignedStores: [], categoryId: '',
  };

  const [formData,   setFormData]   = useState(defaultState);
  const [nuevaTalla, setNuevaTalla] = useState(''); // campo para agregar nueva talla
  const fileInputRef = useRef(null);
  const loadedIdRef  = useRef(null);

  useEffect(() => {
    const incomingId = product?.id ?? null;
    if (incomingId !== null && incomingId === loadedIdRef.current) return;
    loadedIdRef.current = incomingId;

    if (product) {
      setFormData({
        ...defaultState,
        name:              product.name        ?? '',
        description:       product.description ?? '',
        price:             Number(product.price) || 0,
        productionCost:    Number(product.productionCost ?? product.production_cost) || 0,
        lowStockThreshold: Number(product.lowStockThreshold ?? product.low_stock_threshold) || 5,
        variants: Array.isArray(product.variants) && product.variants.length
          ? product.variants.map(v => ({
              size:        v.size  ?? '',
              color:       v.color ?? '',
              stock:       v.stock ?? 0,
              storeStocks: v.storeStocks ?? {},
            }))
          : defaultState.variants,
        images:         Array.isArray(product.images) ? product.images.filter(u => u?.trim()) : [],
        videos:         Array.isArray(product.videos) ? product.videos.filter(u => u?.trim()) : [],
        assignedStores: Array.isArray(product.assignedStores)
          ? product.assignedStores.map(String)
          : product.store_id != null ? [String(product.store_id)] : [],
        categoryId: String(product.categoryId ?? product.category_id ?? ''),
      });
    } else {
      loadedIdRef.current = null;
      setFormData(defaultState);
    }
  }, [product?.id]);

  // ── Cambios generales ────────────────────────────────────────────────────
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['price','productionCost','lowStockThreshold'].includes(name) ? Number(value) : value,
    }));
  };

  const handleStoreAssignment = e => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      assignedStores: checked
        ? [...prev.assignedStores, value]
        : prev.assignedStores.filter(id => id !== value),
    }));
  };

  // ── Variantes ─────────────────────────────────────────────────────────────

  // Agregar nueva talla (con un color vacío inicial)
  const handleAgregarTalla = () => {
    const t = nuevaTalla.trim();
    if (!t) return;
    if (getTallas(formData.variants).includes(t)) {
      alert(`La talla "${t}" ya existe. Usa "+ Color" para agregarle más colores.`);
      return;
    }
    setFormData(prev => ({
      ...prev,
      // Eliminar variantes sin talla (la inicial vacía) al agregar la primera talla real
      variants: [
        ...prev.variants.filter(v => v.size.trim() !== ''),
        { size: t, color: '', stock: 0, storeStocks: {} }
      ],
    }));
    setNuevaTalla('');
  };

  // Agregar nuevo color a una talla existente
  const handleAgregarColor = (talla) => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, { size: talla, color: '', stock: 0, storeStocks: {} }],
    }));
  };

  // Cambiar talla de una variante (renombra todas las variantes de esa talla)
  const handleCambiarTalla = (tallaAnterior, nuevaTallaVal) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map(v =>
        v.size === tallaAnterior ? { ...v, size: nuevaTallaVal } : v
      ),
    }));
  };

  // Cambiar campo de una variante específica (por índice global)
  const handleVariantField = (globalIdx, field, value) => {
    setFormData(prev => {
      const updated = [...prev.variants];
      updated[globalIdx] = {
        ...updated[globalIdx],
        [field]: field === 'stock' ? Number(value) : value,
      };
      return { ...prev, variants: updated };
    });
  };

  // Cambiar stock por tienda
  const handleStoreStock = (globalIdx, storeId, value) => {
    setFormData(prev => {
      const updated = [...prev.variants];
      const newStoreStocks = { ...updated[globalIdx].storeStocks, [storeId]: Number(value) };
      updated[globalIdx] = {
        ...updated[globalIdx],
        storeStocks: newStoreStocks,
        stock: Object.values(newStoreStocks).reduce((s, n) => s + n, 0),
      };
      return { ...prev, variants: updated };
    });
  };

  // Eliminar una variante específica (color dentro de una talla)
  const handleEliminarVariante = (globalIdx) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== globalIdx),
    }));
  };

  // Eliminar toda una talla (todos sus colores)
  const handleEliminarTalla = (talla) => {
    if (!window.confirm(`¿Eliminar la talla "${talla}" y todos sus colores?`)) return;
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter(v => v.size !== talla),
    }));
  };

  // ── Imágenes ──────────────────────────────────────────────────────────────
  const handleImageUpload = async e => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(async file => {
        const form = new FormData();
        form.append('image', file);
        const res  = await fetch(`${API_BASE_URL}/upload_image.php`, {
          method: 'POST', body: form, credentials: 'include',
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.message || 'Error subiendo imagen');
        return json.data.url;
      }));
      setFormData(prev => ({ ...prev, images: [...prev.images, ...urls] }));
    } catch (err) { alert('Error al subir imagen: ' + err.message); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = i => setFormData(prev => ({
    ...prev, images: prev.images.filter((_, idx) => idx !== i),
  }));

  const handleDragStart = (e, i) => e.dataTransfer.setData('imageIndex', i);
  const handleDragOver  = e => e.preventDefault();
  const handleDrop      = (e, dropIdx) => {
    const dragIdx = Number(e.dataTransfer.getData('imageIndex'));
    const imgs    = [...formData.images];
    const [moved] = imgs.splice(dragIdx, 1);
    imgs.splice(dropIdx, 0, moved);
    setFormData(prev => ({ ...prev, images: imgs }));
  };

  const handleVideoAdd = e => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      setFormData(prev => ({ ...prev, videos: [...prev.videos, e.target.value.trim()] }));
      e.target.value = ''; e.preventDefault();
    }
  };
  const removeVideo = i => setFormData(prev => ({
    ...prev, videos: prev.videos.filter((_, idx) => idx !== i),
  }));

  const handleSubmit = e => {
    e.preventDefault();
    // Filtrar variantes sin talla (residuos del estado inicial)
    const variantesValidas = formData.variants.filter(v => v.size.trim() !== '');
    if (variantesValidas.length === 0) {
      alert('Agrega al menos una talla con su color.'); return;
    }
    // Validar que todas tengan color
    const sinColor = variantesValidas.some(v => !v.color.trim());
    if (sinColor) { alert('Todas las variantes deben tener un color asignado.'); return; }
    // Validar duplicados
    const keys = variantesValidas.map(v => `${v.size}|${v.color}`);
    if (new Set(keys).size !== keys.length) {
      alert('Hay combinaciones de talla+color duplicadas. Revisa las variantes.'); return;
    }
    onSubmit({
      ...formData,
      variants: variantesValidas,
      images: formData.images.filter(u => u?.trim()),
      videos: formData.videos.filter(u => u?.trim()),
    });
    if (!product) { loadedIdRef.current = null; setFormData(defaultState); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const tallas = getTallas(formData.variants);

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* ── Información general ─────────────────────────────────────────── */}
      <Section title="Información General">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label style={lbl}>Nombre *</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange}
              style={inp} required onFocus={fr} onBlur={fb} />
          </div>
          <div>
            <label style={lbl}>Categoría</label>
            <select name="categoryId" value={formData.categoryId}
              onChange={e => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
              style={inp} onFocus={fr} onBlur={fb}>
              <option value="">— Sin categoría —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={lbl}>Descripción *</label>
          <textarea name="description" value={formData.description} onChange={handleChange}
            rows={3} style={{ ...inp, resize: 'vertical' }} required onFocus={fr} onBlur={fb} />
        </div>
      </Section>

      {/* ── Precios ─────────────────────────────────────────────────────── */}
      <Section title="Precios y Stock">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <div>
            <label style={lbl}>Precio de venta ($) *</label>
            <input type="number" name="price" value={formData.price} onChange={handleChange}
              style={inp} min="0" step="0.01" required onFocus={fr} onBlur={fb} />
          </div>
          <div>
            <label style={lbl}>Costo de producción ($)</label>
            <input type="number" name="productionCost" value={formData.productionCost}
              onChange={handleChange} style={inp} min="0" step="0.01" onFocus={fr} onBlur={fb} />
          </div>
          <div>
            <label style={lbl}>Umbral stock bajo</label>
            <input type="number" name="lowStockThreshold" value={formData.lowStockThreshold}
              onChange={handleChange} style={inp} min="0" onFocus={fr} onBlur={fb} />
          </div>
        </div>
      </Section>

      {/* ── Tiendas ─────────────────────────────────────────────────────── */}
      <Section title="Tiendas Asignadas">
        {stores.length === 0
          ? <p style={{ ...bs, fontSize: '12px', color: '#bbb' }}>No hay tiendas registradas.</p>
          : <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {stores.map(store => (
                <label key={store.id} style={{ display: 'flex', alignItems: 'center',
                  gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" value={store.id}
                    checked={formData.assignedStores.includes(String(store.id))}
                    onChange={handleStoreAssignment}
                    style={{ accentColor: '#961914', width: '15px', height: '15px' }} />
                  <span style={{ ...bs, fontSize: '13px', color: '#060D14' }}>{store.name}</span>
                </label>
              ))}
            </div>
        }
      </Section>

      {/* ── Variantes ───────────────────────────────────────────────────── */}
      <Section title="Variantes — Tallas y Colores">

        {/* Tallas existentes agrupadas */}
        {tallas.length === 0 && (
          <p style={{ ...bs, fontSize: '12px', color: '#bbb', marginBottom: '16px' }}>
            Agrega una talla para comenzar.
          </p>
        )}

        {tallas.map((talla, tallaIdx) => {
          const variantesDeTalla = formData.variants
            .map((v, gi) => ({ ...v, _gi: gi }))
            .filter(v => v.size === talla);
          const stockTalla = variantesDeTalla.reduce((s, v) =>
            s + Object.values(v.storeStocks || {}).reduce((a, b) => a + b, 0), 0);

          return (
            <div key={talla} style={{ marginBottom: '16px', border: '1px solid #f0eaea',
              borderRadius: '2px', overflow: 'hidden' }}>

              {/* Header talla */}
              <div style={{ background: '#faf9f9', padding: '10px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: '1px solid #f0eaea' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Editar nombre de talla inline */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ ...bs, fontSize: '10px', color: '#bbb',
                      letterSpacing: '0.12em', textTransform: 'uppercase' }}>Talla</span>
                    <input
                      value={talla}
                      onChange={e => handleCambiarTalla(talla, e.target.value)}
                      style={{ ...inp, width: '80px', padding: '5px 8px',
                        fontSize: '13px', fontWeight: 700, color: '#060D14' }}
                      onFocus={fr} onBlur={fb}
                    />
                  </div>
                  <span style={{ ...bs, fontSize: '11px', color: '#bbb' }}>
                    {variantesDeTalla.length} color{variantesDeTalla.length !== 1 ? 'es' : ''} · {stockTalla} uds
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" onClick={() => handleAgregarColor(talla)} style={{
                    ...bs, background: 'none', border: '1px solid #e5c9c9', color: '#961914',
                    padding: '4px 12px', cursor: 'pointer', fontSize: '10px',
                    fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
                    borderRadius: '2px', transition: 'all 0.2s',
                  }}
                  onMouseOver={e => { e.currentTarget.style.background = '#961914'; e.currentTarget.style.color = 'white'; }}
                  onMouseOut={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#961914'; }}>
                    + Color
                  </button>
                  <button type="button" onClick={() => handleEliminarTalla(talla)} style={{
                    ...bs, background: 'none', border: '1px solid #fee2e2', color: '#dc2626',
                    padding: '4px 8px', cursor: 'pointer', fontSize: '10px',
                    borderRadius: '2px', transition: 'all 0.2s',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}>
                    <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Colores de esta talla */}
              {variantesDeTalla.map((v, colorIdx) => (
                <div key={v._gi} style={{
                  padding: '12px 16px',
                  borderBottom: colorIdx < variantesDeTalla.length - 1
                    ? '1px solid #faf5f5' : 'none',
                  background: colorIdx % 2 === 0 ? 'white' : '#fdfafa',
                }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start',
                    flexWrap: 'wrap' }}>

                    {/* Color */}
                    <div style={{ minWidth: '140px', flex: '1' }}>
                      <label style={lbl}>Color *</label>
                      <input type="text" value={v.color}
                        onChange={e => handleVariantField(v._gi, 'color', e.target.value)}
                        style={inp} placeholder="Ej: Rosa Salmón"
                        onFocus={fr} onBlur={fb} />
                    </div>

                    {/* Stock por tienda */}
                    <div style={{ flex: '2', minWidth: '200px' }}>
                      <label style={lbl}>Stock por tienda</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {stores.map(store => (
                          <div key={store.id} style={{ display: 'flex', alignItems: 'center',
                            gap: '6px' }}>
                            <span style={{ ...bs, fontSize: '11px', color: '#888',
                              whiteSpace: 'nowrap' }}>{store.name}:</span>
                            <input type="number" min="0"
                              value={v.storeStocks[store.id] || 0}
                              onChange={e => handleStoreStock(v._gi, store.id, e.target.value)}
                              style={{ ...inp, width: '72px', padding: '6px 8px',
                                textAlign: 'center' }}
                              onFocus={fr} onBlur={fb} />
                          </div>
                        ))}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ ...bs, fontSize: '10px', color: '#bbb' }}>Total:</span>
                          <span style={{ ...bs, fontSize: '13px', fontWeight: 700,
                            color: v.stock > 0 ? '#16a34a' : '#dc2626' }}>
                            {v.stock}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Eliminar este color */}
                    {variantesDeTalla.length > 1 && (
                      <div style={{ paddingTop: '18px' }}>
                        <button type="button" onClick={() => handleEliminarVariante(v._gi)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer',
                            color: '#ddd', padding: '4px', transition: 'color 0.15s' }}
                          onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
                          onMouseOut={e => e.currentTarget.style.color = '#ddd'}>
                          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })}

        {/* Agregar nueva talla */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center',
          padding: '12px', background: '#faf9f9', border: '1px dashed #e5c9c9',
          borderRadius: '2px' }}>
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#961914">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
          </svg>
          <input
            value={nuevaTalla}
            onChange={e => setNuevaTalla(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAgregarTalla(); } }}
            placeholder="Nueva talla (ej: 16.5, M, XL…)"
            style={{ ...inp, flex: 1, padding: '7px 10px' }}
            onFocus={fr} onBlur={fb}
          />
          <button type="button" onClick={handleAgregarTalla} style={{
            ...bs, background: '#961914', color: 'white', border: 'none',
            padding: '8px 18px', cursor: 'pointer', fontSize: '10px',
            fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
            borderRadius: '2px', whiteSpace: 'nowrap', transition: 'background 0.2s',
          }}
          onMouseOver={e => e.currentTarget.style.background = '#7a140f'}
          onMouseOut={e => e.currentTarget.style.background = '#961914'}>
            Agregar Talla
          </button>
        </div>
      </Section>

      {/* ── Imágenes ─────────────────────────────────────────────────────── */}
      <Section title={`Imágenes${uploading ? ' — Subiendo…' : ''}`}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
          {formData.images.map((img, i) => (
            <div key={i} draggable
              onDragStart={e => handleDragStart(e, i)}
              onDragOver={handleDragOver}
              onDrop={e => handleDrop(e, i)}
              style={{ position: 'relative', cursor: 'grab' }}>
              <img src={img} alt="" style={{ width: '88px', height: '88px',
                objectFit: 'cover', borderRadius: '2px', border: '1px solid #f0eaea' }} />
              <button type="button" onClick={() => removeImage(i)} style={{
                position: 'absolute', top: '-6px', right: '-6px',
                background: '#dc2626', color: 'white', border: 'none',
                borderRadius: '50%', width: '18px', height: '18px',
                cursor: 'pointer', fontSize: '11px', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>×</button>
            </div>
          ))}
          <button type="button" onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{ width: '88px', height: '88px', border: '2px dashed #e5c9c9',
              background: 'none', cursor: 'pointer', borderRadius: '2px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#e5c9c9', transition: 'all 0.2s',
              opacity: uploading ? 0.5 : 1 }}
            onMouseOver={e => { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = '#e5c9c9'; e.currentTarget.style.color = '#e5c9c9'; }}>
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4"/>
            </svg>
          </button>
        </div>
        <input type="file" ref={fileInputRef} onChange={handleImageUpload}
          multiple accept="image/*" style={{ display: 'none' }} />
        <p style={{ ...bs, fontSize: '10px', color: '#bbb' }}>
          Arrastra para reordenar · JPG, PNG, WEBP — máx. 5MB c/u
        </p>
      </Section>

      {/* ── Videos ───────────────────────────────────────────────────────── */}
      <Section title="Videos (URL)">
        {formData.videos.map((url, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center',
            gap: '8px', marginBottom: '6px' }}>
            <span style={{ ...bs, fontSize: '11px', color: '#666',
              flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {url}
            </span>
            <button type="button" onClick={() => removeVideo(i)} style={{
              background: 'none', border: 'none', cursor: 'pointer', color: '#ddd' }}
            onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
            onMouseOut={e => e.currentTarget.style.color = '#ddd'}>
              <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        ))}
        <input type="url" placeholder="Pega URL de video y presiona Enter"
          onKeyDown={handleVideoAdd}
          style={inp} onFocus={fr} onBlur={fb} />
      </Section>

      {/* ── Botones ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        {onCancel && (
          <button type="button" onClick={onCancel} style={{
            ...bs, background: 'none', border: '1px solid #e5e0e0', color: '#666',
            padding: '12px 24px', cursor: 'pointer', fontSize: '11px', fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', borderRadius: '2px',
            transition: 'all 0.2s',
          }}
          onMouseOver={e => { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e0e0'; e.currentTarget.style.color = '#666'; }}>
            Cancelar
          </button>
        )}
        <button type="submit" disabled={uploading} style={{
          ...bs, background: '#961914', color: 'white', border: 'none',
          padding: '12px 28px', cursor: uploading ? 'not-allowed' : 'pointer',
          fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em',
          textTransform: 'uppercase', borderRadius: '2px',
          opacity: uploading ? 0.7 : 1, transition: 'background 0.2s',
        }}
        onMouseOver={e => { if (!uploading) e.currentTarget.style.background = '#7a140f'; }}
        onMouseOut={e => e.currentTarget.style.background = '#961914'}>
          {product ? 'Actualizar Producto' : 'Agregar Producto'}
        </button>
      </div>
    </form>
  );
};

export default AdminProductForm;
